import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, AccountAlias, Assignment, Party } from '../api';
import {
  Button,
  Card,
  Checkbox,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function StepAccounts() {
  const qc = useQueryClient();

  const uplines = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });
  const clients = useQuery<Party[]>({
    queryKey: ['parties', 'client'],
    queryFn: () => api.get<Party[]>('/api/parties?role=client'),
  });
  const accounts = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });

  return (
    <div className="space-y-6">
      <AddAccountForm uplines={uplines.data ?? []} clients={clients.data ?? []} />

      <Card>
        <h3 className="mb-3 text-small font-medium text-ink">
          Accounts so far
        </h3>
        {accounts.isLoading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading…
          </div>
        ) : accounts.data && accounts.data.length > 0 ? (
          <ul className="space-y-3">
            {accounts.data.map((a) => (
              <AccountRow
                key={a.id}
                account={a}
                clients={clients.data ?? []}
                onChange={() => qc.invalidateQueries()}
              />
            ))}
          </ul>
        ) : (
          <p className="text-caption text-ink-muted">No accounts yet.</p>
        )}
      </Card>
    </div>
  );
}

function AddAccountForm({
  uplines,
  clients,
}: {
  uplines: Party[];
  clients: Party[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    uplinePartyId: '',
    canonicalName: '',
    panel: '',
    creditLimitLabel: '',
    currencyLabel: 'INR',
    rawName: '',
    clientPartyId: '',
    selfHeld: false,
    clientRate: '',
    uplineRate: '',
    defaultPointsSplit: false,
    effectiveFrom: '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async () => {
      if (!form.uplinePartyId) throw new Error('Pick an upline.');
      if (!form.canonicalName.trim()) throw new Error('Canonical account name is required.');
      if (!form.rawName.trim()) throw new Error('First alias (raw upline name) is required.');
      if (!form.uplineRate.trim()) throw new Error('Upline rate is required.');
      if (!form.selfHeld && !form.clientPartyId) throw new Error('Pick a client or mark self-held.');
      if (!form.selfHeld && !form.clientRate.trim()) throw new Error('Client rate is required.');
      if (!form.effectiveFrom) throw new Error('Effective-from date is required.');
      const account = await api.post<Account>('/api/accounts', {
        uplinePartyId: Number(form.uplinePartyId),
        canonicalName: form.canonicalName.trim(),
        panel: form.panel.trim() || null,
        creditLimitLabel: form.creditLimitLabel.trim() || null,
        currencyLabel: form.currencyLabel.trim() || 'INR',
      });
      await api.post<AccountAlias>(`/api/accounts/${account.id}/aliases`, {
        rawName: form.rawName.trim(),
      });
      await api.post<Assignment>('/api/assignments', {
        accountId: account.id,
        clientPartyId: form.selfHeld ? null : Number(form.clientPartyId),
        clientRate: form.selfHeld ? null : form.clientRate.trim(),
        uplineRate: form.uplineRate.trim(),
        defaultPointsSplit: form.defaultPointsSplit,
        effectiveFrom: form.effectiveFrom,
      });
      return account;
    },
    onSuccess: () => {
      setForm({
        ...form,
        canonicalName: '',
        panel: '',
        creditLimitLabel: '',
        rawName: '',
        clientRate: '',
        uplineRate: '',
      });
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  return (
    <Card>
      <h2 className="text-lg font-semibold">Add an account</h2>
      <p className="mt-1 text-caption text-ink-muted">
        Creates the account, attaches its first alias, and opens an initial
        assignment to a client (or to yourself).
      </p>
      <form
        className="mt-4 grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          createMut.mutate();
        }}
      >
        <Select
          label="Upline"
          value={form.uplinePartyId}
          onChange={(v) => setForm({ ...form, uplinePartyId: v })}
          options={uplines.map((u) => ({ value: String(u.id), label: u.displayName }))}
          placeholder="Choose…"
        />
        <TextInput
          label="Canonical name"
          value={form.canonicalName}
          onChange={(e) => setForm({ ...form, canonicalName: e.target.value })}
          placeholder="e.g. Lotus365Aura"
          hint="Your internal label for this account. Case preserved."
        />
        <TextInput
          label="Panel"
          value={form.panel}
          onChange={(e) => setForm({ ...form, panel: e.target.value })}
          placeholder="e.g. SAURABJI-CLICK"
        />
        <TextInput
          label="Credit limit (label only)"
          value={form.creditLimitLabel}
          onChange={(e) => setForm({ ...form, creditLimitLabel: e.target.value })}
          placeholder="e.g. 50 KG, 2.2 CR"
        />
        <TextInput
          label="First alias (raw upline name)"
          value={form.rawName}
          onChange={(e) => setForm({ ...form, rawName: e.target.value })}
          placeholder="e.g. S ADMIN (LOTUS365AURA)"
          hint="Exact text as it appears on the upline sheet. Case-insensitive."
        />
        <TextInput
          label="Currency label"
          value={form.currencyLabel}
          onChange={(e) => setForm({ ...form, currencyLabel: e.target.value })}
        />

        <div className="col-span-full my-2 border-t border-border pt-3">
          <h3 className="mb-2 text-small font-medium text-ink">Assignment</h3>
          <Checkbox
            label="Self-held"
            hint="Check this if this account is yours directly (no client, no client rate)."
            checked={form.selfHeld}
            onChange={(v) => setForm({ ...form, selfHeld: v })}
          />
        </div>

        {!form.selfHeld && (
          <Select
            label="Client"
            value={form.clientPartyId}
            onChange={(v) => setForm({ ...form, clientPartyId: v })}
            options={clients.map((c) => ({ value: String(c.id), label: c.displayName }))}
            placeholder="Choose…"
          />
        )}
        {!form.selfHeld && (
          <TextInput
            label="Client rate (decimal, e.g. 0.14 = 14%)"
            value={form.clientRate}
            onChange={(e) => setForm({ ...form, clientRate: e.target.value })}
            placeholder="0.14"
            inputMode="decimal"
          />
        )}
        <TextInput
          label="Upline rate (decimal, e.g. 0.06 = 6%)"
          value={form.uplineRate}
          onChange={(e) => setForm({ ...form, uplineRate: e.target.value })}
          placeholder="0.06"
          inputMode="decimal"
        />
        <TextInput
          label="Effective from (Monday date)"
          type="date"
          value={form.effectiveFrom}
          onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
        />
        <div className="col-span-full">
          <Checkbox
            label="Split client points by default"
            hint="Leave off for most accounts. Turn on if this account routinely needs different client vs upline points."
            checked={form.defaultPointsSplit}
            onChange={(v) => setForm({ ...form, defaultPointsSplit: v })}
          />
        </div>

        {error && (
          <div className="col-span-full">
            <ErrorBanner>{error}</ErrorBanner>
          </div>
        )}

        <div className="col-span-full flex items-center gap-2 pt-2">
          <Button type="submit" disabled={createMut.isPending}>
            {createMut.isPending ? <Spinner /> : 'Add account'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function AccountRow({
  account,
  clients,
  onChange,
}: {
  account: Account;
  clients: Party[];
  onChange: () => void;
}) {
  const aliases = useQuery<AccountAlias[]>({
    queryKey: ['aliases', account.id],
    queryFn: () => api.get<AccountAlias[]>(`/api/accounts/${account.id}/aliases`),
  });
  const assignments = useQuery<Assignment[]>({
    queryKey: ['assignments', account.id],
    queryFn: () =>
      api.get<Assignment[]>(`/api/assignments?account=${account.id}`),
  });

  const addAlias = useMutation({
    mutationFn: (rawName: string) =>
      api.post<AccountAlias>(`/api/accounts/${account.id}/aliases`, { rawName }),
    onSuccess: () => {
      onChange();
    },
  });
  const [newAlias, setNewAlias] = useState('');

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-small">
          <span className="font-medium text-ink">{account.canonicalName}</span>
          {account.panel && (
            <span className="ml-2 text-caption text-ink-muted">
              {account.panel}
            </span>
          )}
          {account.creditLimitLabel && (
            <span className="ml-2 text-caption text-ink-muted">
              credit: {account.creditLimitLabel}
            </span>
          )}
        </div>
        <Pill tone={account.status === 'active' ? 'green' : 'amber'}>
          {account.status}
        </Pill>
      </div>

      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-ink-muted">
            Aliases
          </h4>
          <ul className="mt-1 space-y-1 text-caption">
            {aliases.data?.map((a) => (
              <li key={a.id} className="text-ink-muted">
                {a.rawName}
              </li>
            ))}
          </ul>
          <form
            className="mt-2 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const name = newAlias.trim();
              if (!name) return;
              addAlias.mutate(name);
              setNewAlias('');
            }}
          >
            <input
              className="flex-1 rounded-md border border-border bg-surface px-2 py-1 text-caption text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/30"
              placeholder="add another raw alias"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
            />
            <Button type="submit" variant="ghost" disabled={addAlias.isPending}>
              {addAlias.isPending ? <Spinner /> : 'Add'}
            </Button>
          </form>
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-ink-muted">
            Assignments
          </h4>
          <ul className="mt-1 space-y-1 text-caption">
            {assignments.data?.map((asg) => {
              const clientName =
                asg.clientPartyId === null
                  ? 'Self'
                  : (clients.find((c) => c.id === asg.clientPartyId)?.displayName ??
                    `#${asg.clientPartyId}`);
              return (
                <li key={asg.id} className="text-ink-muted">
                  <span className="font-medium">{clientName}</span>{' '}
                  · upline {Number(asg.uplineRate) * 100}%
                  {asg.clientRate && ` · client ${Number(asg.clientRate) * 100}%`}
                  · {asg.effectiveFrom} → {asg.effectiveTo ?? 'open'}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </li>
  );
}
